// What a refusal says when the API gave no words of its own: a { status: false }
// envelope whose message is missing or blank. The customer sees this sentence
// rather than an empty or meaningless line, and it says what to do next.
export const API_ERROR_FALLBACK =
  "Something went wrong. Please try again, or email support@getgoldie.co.nz & we'll sort it."

// The envelope's message when it holds any words, else undefined. `??` alone
// keeps an empty string, and an empty message is a blank error line on screen.
export function envelopeMessage(message: unknown): string | undefined {
  if (typeof message !== 'string') return undefined
  const words = message.trim()
  return words ? words : undefined
}

// FastAPI envelope: { status: boolean, message: string, code?: string, data?: unknown }
export interface ApiResponse<T> {
  status: boolean
  message: string
  code?: string
  data?: T
}

export class ApiError extends Error {
  code: string
  status: number
  details?: unknown

  constructor(
    message: string,
    code: string,
    status: number,
    details?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.details = details
  }

  // Alias for `code`. Trading components branch on `err.errorStatus` against the
  // backend machine codes (trading_is_paused / review_order_expired /
  // not_tradable / insufficient_funds) to show PauseTrading / redirect / toast.
  get errorStatus(): string {
    return this.code
  }

  static fromAxiosError(error: unknown): ApiError {
    // Already an ApiError (e.g. the response interceptor rejected an HTTP-200
    // { status: false } envelope) — pass it through untouched rather than
    // re-wrapping it as UNKNOWN_ERROR and losing its code/status.
    if (error instanceof ApiError) {
      return error
    }
    if (
      typeof error === 'object' &&
      error !== null &&
      'isAxiosError' in error &&
      (error as { isAxiosError: boolean }).isAxiosError
    ) {
      const axiosErr = error as unknown as {
        response?: {
          status: number
          data?: ApiResponse<unknown>
        }
        message: string
      }
      if (axiosErr.response) {
        const { status, data } = axiosErr.response
        const envelope = data as ApiResponse<unknown> | undefined
        const message =
          envelopeMessage(envelope?.message) ??
          axiosErr.message ??
          'Request failed'
        const code = envelope?.code ?? `HTTP_${status}`
        return new ApiError(message, code, status, envelope?.data)
      }
      // Network error — no response
      return new ApiError(
        axiosErr.message ?? 'Network error',
        'NETWORK_ERROR',
        0
      )
    }
    // Non-axios error
    const message =
      error instanceof Error ? error.message : 'An unknown error occurred'
    return new ApiError(message, 'UNKNOWN_ERROR', 500)
  }
}
